import React, { Component, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { useIntl, FormattedMessage } from '../../util/reactIntl';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
} from '../../util/search';
import { parse, stringify } from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes } from '../../util/types';
import { showCreateListingLinkForUser } from '../../util/userHelpers';
import { makeGetListingsByIdSelector } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { toggleInWishlist } from '../../ducks/user.duck';

import { H3, H5, Page, ResponsiveImage } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  initialValues,
  validUrlQueryParamsFromProps,
  onResetAll,
  createFilterValueChangeHandler,
  onSortBy,
  searchParamsPicker,
  pickListingFieldFilters,
  groupListingFieldConfigs,
  createSearchResultSchema,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import { getSeoConfig, getPageOverride } from './seo';
import SeoIntro from './seo/SeoIntro';
import SeoBottomSection from './seo/SeoBottomSection';
import { resolveSlug, findSlug, resolveCollectionSlug } from './seo/categorySlugs';
import SearchPageAccessWrapper from './SearchPageAccessWrapper';
import SearchErrors from './SearchErrors';

import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

// Add ViewAllButton component
const ViewAllButton = ({ onClick, label = 'View All' }) => (
  <button
    className={css.viewAllButton}
    onClick={onClick}
  >
    {label}
  </button>
);

// Add FilterIcon component
const FilterIcon = ({ className }) => (
  <svg 
    className={className}
    width="16" 
    height="16" 
    viewBox="0 0 16 16" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M2 3h12l-4 4.5V12l-4 2V7.5L2 3z" 
      stroke="currentColor" 
      strokeWidth="1.5" 
      fill="none" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

// Add DesktopFilterButton component
const DesktopFilterButton = ({ onClick, isOpen, selectedFiltersCount = 0 }) => (
  <button
    className={classNames(css.desktopFilterButton, {
      [css.desktopFilterButtonActive]: isOpen || selectedFiltersCount > 0
    })}
    onClick={onClick}
  >
    <FilterIcon className={css.filterIcon} />
    <FormattedMessage
      id="SearchFiltersMobile.filtersButtonLabel"
    />
    {selectedFiltersCount > 0 && (
      <span className={css.filterCount}>({selectedFiltersCount})</span>
    )}
  </button>
);

// Hand drawn arrow SVG component
const HandDrawnArrow = () => (
  <svg
    viewBox="0 0 100 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    stroke="currentColor"
    strokeWidth="15"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="inline-block ml-1 w-5 h-5"
  >
    <line x1="50" y1="10" x2="50" y2="100" />
    <polyline points="35 85 50 100 65 85" />
  </svg>
);

// Remove the hardcoded COLLECTIONS array and replace with a function to transform CMS data
const transformCollectionsFromCMS = (pageAssetsData) => {
  if (!pageAssetsData?.landingPage?.data?.sections) return [];
  
  const collectionsSection = pageAssetsData.landingPage.data.sections.find(
    section => section.sectionName?.toLowerCase() === 'collections'
  );

  if (!collectionsSection?.blocks) return [];

  return collectionsSection.blocks.map(block => {
    // Extract collection value from the href
    // Supports both clean URLs (/s/art/gedolim) and query params (?meta_collection=...)
    const href = block.callToAction?.href || '';
    let collectionValue = '';
    if (href.includes('?')) {
      collectionValue = new URLSearchParams(href.split('?')[1]).get('meta_collection') || '';
    } else {
      // Clean URL: extract last path segment as the category slug
      const segments = href.split('/').filter(Boolean);
      // Expect /s/art/:category — the slug is the 3rd segment
      collectionValue = segments.length >= 3 ? segments[2] : '';
    }

    return {
      label: block.title?.content || '',
      value: collectionValue || '',
      description: block.text?.content || '',
      image: block.media?.image?._ref || null
    };
  });
};

// Transform Judaica collections from CMS data
const transformJudaicaCollectionsFromCMS = (pageAssetsData) => {
  if (!pageAssetsData?.landingPage?.data?.sections) return [];
  
  const judaicaCollectionsSection = pageAssetsData.landingPage.data.sections.find(
    section => section.sectionName?.toLowerCase() === 'judaica collections'
  );

  if (!judaicaCollectionsSection?.blocks) return [];

  return judaicaCollectionsSection.blocks.map(block => {
    // Extract collection value from the href
    // Supports both clean URLs (/s/judaica/mezuzas) and query params (?pub_category_j=...)
    const href = block.callToAction?.href || '';
    let collectionValue = '';
    if (href.includes('?')) {
      collectionValue = new URLSearchParams(href.split('?')[1]).get('pub_category_j') || '';
    } else {
      // Clean URL: extract last path segment as the category slug
      const segments = href.split('/').filter(Boolean);
      // Expect /s/judaica/:category — the slug is the 3rd segment
      collectionValue = segments.length >= 3 ? segments[2] : '';
    }

    return {
      label: block.title?.content || '',
      value: collectionValue || '',
      image: block.media?.image || null // Include full image object for Judaica
    };
  });
};

// Update CollectionButtons to use transformed data and show description
const CollectionButtons = ({ activeCollection, onSelectCollection, onViewAll, collections }) => {
  const containerId = 'collection-buttons-container';
  const sliderId = 'collection-buttons-slider';
  const trackId = 'collection-buttons-track';
  
  // Add state for arrows visibility
  const [showArrows, setShowArrows] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  // Get active collection data
  const activeCollectionData = collections.find(c => c.value === activeCollection);

  // Function to check scroll position and update arrows
  const updateArrowVisibility = (slider) => {
    if (!slider) return;
    
    // Check if we can scroll left (not at start)
    setShowLeftArrow(slider.scrollLeft > 0);
    
    // Check if we can scroll right (not at end)
    const canScrollRight = slider.scrollLeft < (slider.scrollWidth - slider.clientWidth - 2); // -2 for rounding errors
    setShowRightArrow(canScrollRight);
  };

  useEffect(() => {
    const setCarouselWidth = () => {
      const elem = window.document.getElementById(containerId);
      const track = window.document.getElementById(trackId);
      const slider = window.document.getElementById(sliderId);
      
      if (elem && track && slider) {
        const scrollbarWidth = window.innerWidth - document.body.clientWidth;
        const elementWidth = elem.clientWidth;
        const carouselWidth = elementWidth - scrollbarWidth;
        elem.style.setProperty('--carouselWidth', `${carouselWidth}px`);

        // Check if content is wider than container
        const needsScrolling = track.scrollWidth > carouselWidth;
        setShowArrows(needsScrolling);
        
        // Initialize arrow visibility
        updateArrowVisibility(slider);
      }
    };

    // Initial setup with a small delay to ensure DOM is ready
    setTimeout(setCarouselWidth, 100);

    // Scroll to active collection on mount
    const scrollToActive = () => {
      if (activeCollection) {
        const slider = document.getElementById(sliderId);
        const activeButton = slider?.querySelector(`[data-value="${activeCollection}"]`);
        if (slider && activeButton) {
          const sliderRect = slider.getBoundingClientRect();
          const buttonRect = activeButton.getBoundingClientRect();
          const scrollLeft = buttonRect.left - sliderRect.left - (sliderRect.width - buttonRect.width) / 2;
          slider.scrollLeft = slider.scrollLeft + scrollLeft;
          
          // Update arrows after scrolling to active
          updateArrowVisibility(slider);
        }
      }
    };
    
    setTimeout(scrollToActive, 200);

    window.addEventListener('resize', setCarouselWidth);
    return () => window.removeEventListener('resize', setCarouselWidth);
  }, [activeCollection, collections]); // Add collections to dependency array

  // Add scroll event listener to update arrows during manual scrolling
  useEffect(() => {
    const slider = document.getElementById(sliderId);
    if (slider) {
      const handleScroll = () => updateArrowVisibility(slider);
      slider.addEventListener('scroll', handleScroll);
      return () => slider.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const onSlideLeft = () => {
    const slider = window.document.getElementById(sliderId);
    if (slider) {
      const slideWidth = slider.clientWidth * 0.8;
      slider.scrollLeft = slider.scrollLeft - slideWidth;
      setTimeout(() => updateArrowVisibility(slider), 100);
    }
  };

  const onSlideRight = () => {
    const slider = window.document.getElementById(sliderId);
    if (slider) {
      const slideWidth = slider.clientWidth * 0.8;
      slider.scrollLeft = slider.scrollLeft + slideWidth;
      setTimeout(() => updateArrowVisibility(slider), 100);
    }
  };

  return (
    <div className={css.collectionsContainer}>
      <div className={css.collectionsContent}>
        <div className={css.collectionsHeader}>
          <h2 className={css.collectionsTitle}>Collections</h2>
          {!!activeCollection && (
            <ViewAllButton onClick={onViewAll} />
          )}
        </div>
        <div className={css.carouselContainer} id={containerId}>
          {showArrows && (
            <div className={css.carouselArrows}>
              {showLeftArrow && (
                <button className={css.carouselArrowPrev} onClick={onSlideLeft}>
                  ‹
                </button>
              )}
              {showRightArrow && (
                <button className={css.carouselArrowNext} onClick={onSlideRight}>
                  ›
                </button>
              )}
            </div>
          )}
          <div className={css.carouselSlider} id={sliderId}>
            <div className={css.carouselTrack} id={trackId}>
              {collections.map(collection => (
                <button
                  key={collection.value}
                  data-value={collection.value}
                  className={classNames(css.collectionButton, {
                    [css.activeCollection]: activeCollection === collection.value
                  })}
                  onClick={() => onSelectCollection(collection.value)}
                >
                  {collection.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        {activeCollectionData && (
          <div className={css.collectionDescription}>
            {activeCollectionData.description}
          </div>
        )}
      </div>
    </div>
  );
};

// Judaica Collection Buttons with Images
const JudaicaCollectionButtons = ({ activeCollection, onSelectCollection, onViewAll, collections, title = 'Collections' }) => {
  const containerId = 'judaica-collection-buttons-container';
  const sliderId = 'judaica-collection-buttons-slider';
  const trackId = 'judaica-collection-buttons-track';
  
  // Add state for arrows visibility
  const [showArrows, setShowArrows] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  // Function to check scroll position and update arrows
  const updateArrowVisibility = (slider) => {
    if (!slider) return;
    
    // Check if we can scroll left (not at start)
    setShowLeftArrow(slider.scrollLeft > 0);
    
    // Check if we can scroll right (not at end)
    const canScrollRight = slider.scrollLeft < (slider.scrollWidth - slider.clientWidth - 2);
    setShowRightArrow(canScrollRight);
  };

  useEffect(() => {
    const setCarouselWidth = () => {
      const elem = window.document.getElementById(containerId);
      const track = window.document.getElementById(trackId);
      const slider = window.document.getElementById(sliderId);
      
      if (elem && track && slider) {
        const scrollbarWidth = window.innerWidth - document.body.clientWidth;
        const elementWidth = elem.clientWidth;
        const carouselWidth = elementWidth - scrollbarWidth;
        elem.style.setProperty('--carouselWidth', `${carouselWidth}px`);

        // Check if content is wider than container
        const needsScrolling = track.scrollWidth > carouselWidth;
        setShowArrows(needsScrolling);
        
        // Initialize arrow visibility
        updateArrowVisibility(slider);
      }
    };

    // Initial setup with a small delay to ensure DOM is ready
    setTimeout(setCarouselWidth, 100);

    // Scroll to active collection on mount
    const scrollToActive = () => {
      if (activeCollection) {
        const slider = document.getElementById(sliderId);
        const activeButton = slider?.querySelector(`[data-value="${activeCollection}"]`);
        if (slider && activeButton) {
          const sliderRect = slider.getBoundingClientRect();
          const buttonRect = activeButton.getBoundingClientRect();
          const scrollLeft = buttonRect.left - sliderRect.left - (sliderRect.width - buttonRect.width) / 2;
          slider.scrollLeft = slider.scrollLeft + scrollLeft;
          
          // Update arrows after scrolling to active
          updateArrowVisibility(slider);
        }
      }
    };
    
    setTimeout(scrollToActive, 200);

    window.addEventListener('resize', setCarouselWidth);
    return () => window.removeEventListener('resize', setCarouselWidth);
  }, [activeCollection, collections]);

  // Add scroll event listener to update arrows during manual scrolling
  useEffect(() => {
    const slider = document.getElementById(sliderId);
    if (slider) {
      const handleScroll = () => updateArrowVisibility(slider);
      slider.addEventListener('scroll', handleScroll);
      return () => slider.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const onSlideLeft = () => {
    const slider = window.document.getElementById(sliderId);
    if (slider) {
      const slideWidth = slider.clientWidth * 0.8;
      slider.scrollLeft = slider.scrollLeft - slideWidth;
      setTimeout(() => updateArrowVisibility(slider), 100);
    }
  };

  const onSlideRight = () => {
    const slider = window.document.getElementById(sliderId);
    if (slider) {
      const slideWidth = slider.clientWidth * 0.8;
      slider.scrollLeft = slider.scrollLeft + slideWidth;
      setTimeout(() => updateArrowVisibility(slider), 100);
    }
  };

  return (
    <div className={css.collectionsContainer}>
      <div className={css.collectionsContent}>
        <div className={css.collectionsHeader}>
          <h2 className={css.collectionsTitle}>{title}</h2>
          {!!activeCollection && (
            <ViewAllButton onClick={onViewAll} label="View All Judaica" />
          )}
        </div>
        <div className={css.carouselContainer} id={containerId}>
          {showArrows && (
            <div className={css.carouselArrows}>
              {showLeftArrow && (
                <button className={css.carouselArrowPrev} onClick={onSlideLeft}>
                  ‹
                </button>
              )}
              {showRightArrow && (
                <button className={css.carouselArrowNext} onClick={onSlideRight}>
                  ›
                </button>
              )}
            </div>
          )}
          <div className={css.carouselSlider} id={sliderId}>
            <div className={css.carouselTrack} id={trackId}>
              {collections.map(collection => {
                // Get available image variants for ResponsiveImage
                const imageVariants = collection.image?.attributes?.variants || {};
                const variantNames = Object.keys(imageVariants).filter(name => name.startsWith('original'));

                return (
                  <button
                    key={collection.value}
                    data-value={collection.value}
                    className={classNames(css.judaicaCollectionButton, {
                      [css.activeCollection]: activeCollection === collection.value
                    })}
                    onClick={() => onSelectCollection(collection.value)}
                  >
                    {/* Small Image on Left */}
                    {collection.image && (
                      <div className={css.judaicaCollectionImage}>
                        <ResponsiveImage
                          className={css.judaicaCollectionImageContent}
                          image={collection.image}
                          alt={collection.label}
                          variants={variantNames.length > 0 ? variantNames : ['square400']}
                          sizes="48px"
                        />
                      </div>
                    )}
                    
                    {/* Title */}
                    <span className={css.judaicaCollectionLabel}>
                      {collection.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      collections: [],
      judaicaCollections: [],
      isDesktopFiltersOpen: false
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);
    this.toggleDesktopFilters = this.toggleDesktopFilters.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
  }

  componentDidMount() {
    // Transform collections data when component mounts or when pageAssetsData updates
    const collections = transformCollectionsFromCMS(this.props.pageAssetsData);
    const judaicaCollections = transformJudaicaCollectionsFromCMS(this.props.pageAssetsData);
    this.setState({ collections, judaicaCollections });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.pageAssetsData !== this.props.pageAssetsData) {
      const collections = transformCollectionsFromCMS(this.props.pageAssetsData);
      const judaicaCollections = transformJudaicaCollectionsFromCMS(this.props.pageAssetsData);
      this.setState({ collections, judaicaCollections });
    }

  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Toggle desktop filters sidebar
  toggleDesktopFilters() {
    this.setState(prevState => ({ 
      isDesktopFiltersOpen: !prevState.isDesktopFiltersOpen 
    }));
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config, location } = this.props;
    onResetAll({
      history,
      routeConfiguration,
      config,
      location,
      urlQueryParams: validUrlQueryParamsFromProps(this.props),
      setState: this.setState.bind(this),
    });
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;

    return createFilterValueChangeHandler(
      {
        history,
        routeConfiguration,
        config,
        location,
        currentPathParams,
        urlQueryParams: validUrlQueryParamsFromProps(this.props),
        setState: this.setState.bind(this),
        getState: () => this.state,
      },
      useHistoryPush
    );
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    onSortBy({
      history,
      routeConfiguration,
      location,
      urlQueryParams: validUrlQueryParamsFromProps(this.props),
      urlParam,
      values,
    });
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  handleCollectionChange = collectionValue => {
    const { location, history, routeConfiguration } = this.props;
    const parsedSearchParams = parse(location.search);

    // Try to use a clean URL slug
    const slug = findSlug('art', 'meta_collection', collectionValue);
    if (slug) {
      const updatedParams = omit(parsedSearchParams, ['meta_collection', 'page']);
      history.replace(
        createResourceLocatorString('SearchPageWithCategory', routeConfiguration, { listingType: 'art', category: slug }, updatedParams)
      );
    } else {
      const updatedParams = { ...parsedSearchParams, meta_collection: collectionValue };
      delete updatedParams.page;
      history.replace(
        createResourceLocatorString('SearchPageWithListingType', routeConfiguration, { listingType: 'art' }, updatedParams)
      );
    }
  };

  handleJudaicaCollectionChange = collectionValue => {
    const { location, history, routeConfiguration } = this.props;
    const parsedSearchParams = parse(location.search);

    // Try to use a clean URL slug
    const slug = findSlug('judaica', 'pub_category_j', collectionValue);
    if (slug) {
      const updatedParams = omit(parsedSearchParams, ['pub_category_j', 'page']);
      history.replace(
        createResourceLocatorString('SearchPageWithCategory', routeConfiguration, { listingType: 'judaica', category: slug }, updatedParams)
      );
    } else {
      const updatedParams = { ...parsedSearchParams, pub_category_j: collectionValue };
      delete updatedParams.page;
      history.replace(
        createResourceLocatorString('SearchPageWithListingType', routeConfiguration, { listingType: 'judaica' }, updatedParams)
      );
    }
  };

  handleViewAll = () => {
    const { location, history, routeConfiguration } = this.props;
    const parsedSearchParams = parse(location.search);
    const updatedParams = omit(parsedSearchParams, ['meta_collection', 'page']);

    // Navigate back to listing type page (no category)
    history.push(createResourceLocatorString('SearchPageWithListingType', routeConfiguration, { listingType: 'art' }, updatedParams));
  };

  handleJudaicaViewAll = () => {
    const { location, history, routeConfiguration } = this.props;
    const parsedSearchParams = parse(location.search);
    const updatedParams = omit(parsedSearchParams, ['pub_category_j', 'page']);

    // Navigate back to listing type page (no category)
    history.push(createResourceLocatorString('SearchPageWithListingType', routeConfiguration, { listingType: 'judaica' }, updatedParams));
  };

  handleCrossCollectionChange = collectionSlug => {
    const { history, routeConfiguration } = this.props;
    history.push(
      createResourceLocatorString('SearchPageWithCollection', routeConfiguration, { collectionSlug }, {})
    );
  };

  

  render() {
    const {
      intl,
      listings = [],
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      routeConfiguration,
      config,
      pageAssetsData,
      currentUser,
      onToggleInWishlist,
      params: currentPathParams = {}
    } = this.props;

    // If the search page variant is of type /s/:listingType, this defines the :listingType
    // path parameter used to filter the whole page.
    //
    // On a default search page (/s), this constant does not have a value, even when a
    // query parameter ?pub_listingType=[queryParamListingType] is used.
    const { listingType: listingTypePathParam, category: categorySlug, collectionSlug: collectionSlugParam } = currentPathParams;

    // Resolve clean category slug (e.g. /s/judaica/mezuzahs) into its query param
    const resolvedCategory = categorySlug
      ? resolveSlug(listingTypePathParam, categorySlug)
      : null;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersRaw, sortConfig } = config?.search || {};

    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const defaultFiltersConfig = listingTypePathParam
      ? defaultFiltersRaw.filter(f => f.key !== 'listingType')
      : defaultFiltersRaw;

    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
      activeListingTypes,
      currentPathParams,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      isOriginInUse(config)
    );
    const validQueryParams = urlQueryParams;

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel', 'listingType'].includes(f.key)
    );
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(
          f => !['keywords', 'categoryLevel', 'listingType'].includes(f.key)
        )
      : defaultFiltersConfig.filter(f => !['categoryLevel', 'listingType'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const collectionFilterConfig = customPrimaryFilters.find(f => f.key === 'collection');
    const remainingCustomPrimaryFilters = customPrimaryFilters.filter(f => f.key !== 'collection');
    const availableFilters = [
      ...builtInPrimaryFilters,
      ...remainingCustomPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ];

    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );

    // Joodaic computes title/description/schema below from SEO config; only need showCreateListingsLink here.
    const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);

    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          labelId={`${mode}-search-page-sort-by`}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        showCreateListingsLink={showCreateListingsLink}
      />
    );

    const searchResult = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    const parsedSearchParams = parse(location.search);
    // Category can come from either query params OR clean URL path params
    const activeCollection =
      parsedSearchParams.meta_collection ||
      (resolvedCategory?.param === 'meta_collection' ? resolvedCategory.value : null);
    const activeJudaicaCollection =
      parsedSearchParams.pub_category_j ||
      (resolvedCategory?.param === 'pub_category_j' ? resolvedCategory.value : null);
    const isViewAllPage = !activeCollection && !activeJudaicaCollection && !categorySlug;

    // Category-specific SEO (schema, title, description, intro/bottom sections)
    const seoConfig = getSeoConfig({
      listingType: listingTypePathParam,
      activeCollection,
      activeJudaicaCollection,
      collectionSlug: collectionSlugParam,
    });
    const pageOverride = getPageOverride(seoConfig, config.marketplaceRootURL);

    const title = pageOverride?.title || searchResult.title;
    const description = pageOverride?.description || searchResult.description;
    const schema = pageOverride?.schema || searchResult.schema;


    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // Compute canonical path for category pages:
    // Always point to the clean URL (/s/:listingType/:category), stripping any filter params.
    // This fixes split ranking power when the same category is reachable via both clean URL
    // and query params (e.g. ?pub_category_j=has_all:mezuzas).
    let searchPageCanonicalPath;
    if (categorySlug && listingTypePathParam) {
      // Already on clean URL path (/s/judaica/mezuzas) — strip any extra filter query params
      searchPageCanonicalPath = `/s/${listingTypePathParam}/${categorySlug}`;
    } else if (listingTypePathParam) {
      // Category is in query params — resolve to the clean URL equivalent if possible
      const judaicaSlug = parsedSearchParams.pub_category_j
        ? findSlug('judaica', 'pub_category_j', parsedSearchParams.pub_category_j)
        : null;
      const artSlug = parsedSearchParams.meta_collection
        ? findSlug('art', 'meta_collection', parsedSearchParams.meta_collection)
        : null;
      if (judaicaSlug) {
        searchPageCanonicalPath = `/s/judaica/${judaicaSlug}`;
      } else if (artSlug) {
        searchPageCanonicalPath = `/s/art/${artSlug}`;
      }
    }

    // Noindex paginated search results (page > 1), empty results, and faceted/filter URLs.
    // Faceted filter URLs (e.g. ?pub_j_materials=has_all:resin) dilute ranking authority
    // and confuse search engines — only the clean category URL should be indexed.
    const currentPage = Number(parse(location.search).page || 1);
    const hasNoResults = listingsAreLoaded && totalItems === 0;
    // Category/collection params and sort are not faceted filters — everything else is
    const CANONICAL_PARAM_KEYS = ['pub_category_j', 'meta_collection', 'pub_listingType', 'pub_collection', 'sort'];
    const hasFacetedFilters = Object.keys(validQueryParams).some(
      key => !CANONICAL_PARAM_KEYS.includes(key)
    );
    // SEO landing pages (with a seoConfig) are intentional collection pages — never noindex
    // them for being empty or for their own defining filter params. Only noindex on page > 1.
    const shouldNoIndex = seoConfig
      ? currentPage > 1
      : currentPage > 1 || hasNoResults || hasFacetedFilters;
    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
        noIndex={shouldNoIndex}
        canonicalPath={searchPageCanonicalPath}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        <div className={css.layoutWrapperContainer}>
          {/* Show sidebar filters when desktop filters are open */}
          {this.state.isDesktopFiltersOpen ? (
            <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
              <div className={css.filterColumnContent}>
                {availableFilters.map(filterConfig => {
                  const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${
                    filterConfig.key
                  }`;
                  const filterId = `SearchFiltersDesktop.${filterConfig.key.toLowerCase()}`;
                  return (
                    <FilterComponent
                      key={key}
                      id={filterId}
                      idPrefix="SearchFiltersDesktop"
                      className={css.filter}
                      config={filterConfig}
                      containerId="SearchPageWithGrid_DesktopFilters"
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                      isDesktop
                      appendedCollectionConfig={
                        ['art_category', 'category_j'].includes(filterConfig.key)
                          ? collectionFilterConfig
                          : undefined
                      }
                      onCrossCollectionSelect={this.handleCrossCollectionChange}
                    />
                  );
                })}
                <button className={css.resetAllButton} onClick={e => this.handleResetAll(e)}>
                  <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
                </button>
              </div>
            </aside>
          ) : null}

          <div
            id="main-content"
            className={classNames(css.layoutWrapperMain, {
              [css.layoutWrapperMainWithSidebar]: this.state.isDesktopFiltersOpen,
            })}
            role="main"
          >
            <div className={css.searchResultContainer}>

              {/* Category SEO intro — collapses when user is actively browsing/filtering */}
              {seoConfig && (
                <SeoIntro
                  seoConfig={seoConfig}
                  collapsed={
                    (pagination?.page && pagination.page > 1) ||
                    // Exclude params that define the SEO page itself from the filter count
                    Object.keys(validQueryParams).filter(k =>
                      !['pub_category_j', 'meta_collection', 'pub_collection', 'pub_sign_type'].includes(k)
                    ).length > 0
                  }
                />
              )}

              {/* Desktop filter button - only show on desktop */}
              {isViewAllPage && (
              <div className={css.desktopFilterContainer}>
                <DesktopFilterButton
                  onClick={this.toggleDesktopFilters}
                  isOpen={this.state.isDesktopFiltersOpen}
                  selectedFiltersCount={selectedFiltersCountForMobile}
                />
              </div>
            )}

              {/* Show art collections when on art page */}
              {listingTypePathParam === 'art' && (
                <CollectionButtons
                  activeCollection={activeCollection}
                  onSelectCollection={this.handleCollectionChange}
                  onViewAll={this.handleViewAll}
                  collections={this.state.collections}
                />
              )}

              {/* Show judaica collections when on judaica page */}
              {listingTypePathParam === 'judaica' && (
                <JudaicaCollectionButtons
                  activeCollection={activeJudaicaCollection}
                  onSelectCollection={this.handleJudaicaCollectionChange}
                  onViewAll={this.handleJudaicaViewAll}
                  collections={this.state.judaicaCollections}
                  title="Collections"
                />
              )}
              
              <SearchFiltersMobile
                className={css.searchFiltersMobileList}
                urlQueryParams={validQueryParams}
                sortByComponent={sortBy('mobile')}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={this.onOpenMobileModal}
                onCloseModal={this.onCloseMobileModal}
                resetAll={this.resetAll}
                selectedFiltersCount={selectedFiltersCountForMobile}
                isMapVariant={false}
                noResultsInfo={noResultsInfo}
                location={location}
                onToggleDesktopFilters={this.toggleDesktopFilters}
                isDesktopFiltersOpen={this.state.isDesktopFiltersOpen}
              >
                {availableFilters.map(filterConfig => {
                  const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                    filterConfig.key
                  }`;
                  const filterId = `SearchFiltersMobile.${filterConfig.key.toLowerCase()}`;

                  return (
                    <FilterComponent
                      key={key}
                      id={filterId}
                      config={filterConfig}
                      containerId="SearchPage_MobileFilters"
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                      appendedCollectionConfig={['art_category', 'category_j'].includes(filterConfig.key) ? collectionFilterConfig : undefined}
                      onCrossCollectionSelect={this.handleCrossCollectionChange}
                    />
                  );
                })}
              </SearchFiltersMobile>
              <MainPanelHeader
                className={css.mainPanel}
                sortByComponent={sortBy('desktop')}
                isSortByActive={sortConfig.active}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                noResultsInfo={noResultsInfo}
              />
              <div
                className={classNames(css.listingsForGridVariant, {
                  [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                })}
              >
                <SearchErrors
                  searchListingsError={searchListingsError}
                  isValidDatesFilter={isValidDatesFilter}
                />
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={listings}
                  pagination={listingsAreLoaded ? pagination : null}
                  search={parse(location.search)}
                  isMapVariant={false}
                  currentUser={currentUser}
                  onToggleWishlist={onToggleInWishlist}
                  listingTypeParam={listingTypePathParam}
                  intl={intl}
                  searchTrackingData={{
                    search_query: validQueryParams.keywords || '',
                    active_filters: JSON.stringify(selectedFilters),
                    page_number: validQueryParams.page || 1,
                    collection: activeCollection || activeJudaicaCollection || null,
                    listing_type_filter: listingTypePathParam || 'all',
                    total_results: totalItems,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        {seoConfig && <SeoBottomSection seoConfig={seoConfig} />}
        <FooterContainer />
      </Page>
    );
  }
}
/**
 * SearchPage "container" (grid layout): selects Redux state and dispatch handlers, then passes the
 * same prop surface as before to `SearchPageComponent` via `SearchPageAccessWrapper`.
 *
 * @param {Object} props - Router / route props from `routeConfiguration.js` and `Routes.js`
 * @returns {JSX.Element}
 */
const SearchPage = props => {
  const dispatch = useDispatch();
  const selectListingsById = useMemo(makeGetListingsByIdSelector, []);

  const location = useLocation();

  const currentUser = useSelector(state => state.user?.currentUser);
  const { pagination, searchInProgress, searchListingsError, searchParams } = useSelector(
    state => state.SearchPage
  );
  const listings = useSelector(state =>
    selectListingsById(state, state.SearchPage.currentPageResultIds)
  );
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));
  const pageAssetsData = useSelector(state => state.hostedAssets?.pageAssetsData);

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );
  const onToggleInWishlist = useCallback(
    (listingId, isInWishlist) => dispatch(toggleInWishlist(listingId, isInWishlist)),
    [dispatch]
  );

  // When using a clean category URL (e.g. /s/judaica/mezuzas), inject the
  // resolved category param into location.search so all existing param-parsing
  // logic inside SearchPageComponent works without modification.
  // Also handles /s/collection/:collectionSlug → pub_collection=has_all:slug (cross-listing-type).
  const { category, listingType, collectionSlug } = props.params || {};
  const resolved = category && listingType
    ? resolveSlug(listingType, category)
    : collectionSlug
    ? resolveCollectionSlug(collectionSlug)
    : null;
  const effectiveLocation = resolved
    ? {
        ...location,
        search: `${location.search}${location.search ? '&' : '?'}${resolved.param}=${encodeURIComponent(
          resolved.value
        )}`,
      }
    : location;

  return (
    <SearchPageAccessWrapper
      {...props}
      PageComponent={SearchPageComponent}
      location={effectiveLocation}
      currentUser={currentUser}
      listings={listings}
      pagination={pagination}
      scrollingDisabled={scrollingDisabled}
      searchInProgress={searchInProgress}
      searchListingsError={searchListingsError}
      searchParams={searchParams}
      pageAssetsData={pageAssetsData}
      onManageDisableScrolling={onManageDisableScrolling}
      onToggleInWishlist={onToggleInWishlist}
    />
  );
};

export default SearchPage;
